import BasicButton from "@components/button/BasicButton";
import CustomTextInput from "@components/input/CustomTextInput";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "@libs/zod/zodVaildation";
import { ConfigProvider, Flex, Tooltip } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import GoogleLoginButton from "@components/button/GoogleLoginButton";
import { useMutation } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useEffect, useState } from "react";
import AppleLoginButton from "@components/button/AppleLoginButton";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import useModalStore from "@store/modalStore";

interface LoginFormData {
  login_id: string;
  password: string;
  // fcm_token: string;
}

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { postLogin, postVerifyEmail } = useApiRequest();
  const [recentLogin, setRecentLogin] = useState<string | null>("");
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [suspendedModalVisible, setSuspendedModalVisible] = useState(false);
  const isAndroid = () => /Android/i.test(navigator.userAgent);
  // const [androidToken, setAndroidToken] = useState("");
  // const [iosToken, setIosToken] = useState("");
  const language = window.localStorage.getItem("language") || "ko";
  const { modals, openModal, closeModal } = useModalStore();
  // 이메일 검증
  const { mutate: signupEmailMutation } = useMutation({
    mutationFn: (email: string) => postVerifyEmail(email, language),
    onSuccess: () => {
      closeModal("activeUser");
    },
  });

  const mutation = useMutation({
    mutationFn: (data: LoginFormData) => postLogin(data),
    onSuccess: (response) => {
      // 액세스 토큰을 localStorage에 저장 (Authorization 헤더용)
      const accessToken = response?.data?.data?.access_token;
      if (accessToken) {
        localStorage.setItem("accessToken", accessToken);
      }
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginType", "NORMAL");
      navigate("/");
    },

    onError: (error: any) => {
      setError("password", {
        type: "server",
        message: "ID/PW 가 일치하지 않습니다.",
      });

      if (error.response.data.data.error_code === "1011") {
        setDeactivateModal(true);
      } else if (error.response.data.data.error_code === "1013") {
        setSuspendedModalVisible(true);
      } else if (error.response.data.data.error_code === "1015") {
        openModal("activeUser");
      }
      return null;
    },
  });

  // android call
  // useEffect(() => {
  //   if (window.android) {
  //     const fcmToken = window.android.getFCMToken() || "";
  //     setAndroidToken(fcmToken);
  //   }
  // }, []);

  // ios call
  // useEffect(() => {
  //   const eventFromIos = async (event: any) => {
  //     setIosToken(event.detail.data);
  //   };

  //   window.addEventListener("getFcmTokenCallback", eventFromIos);

  //   if (window.webkit) {
  //     window.webkit.messageHandlers.getFcmToken.postMessage("");
  //   }

  //   return () =>
  //     window.removeEventListener("getFcmTokenCallback", eventFromIos);
  // });

  useEffect(() => {
    const loginType = localStorage.getItem("loginType");
    setRecentLogin(loginType);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    // const fcmToken = isAndroid() ? androidToken : iosToken;

    mutation.mutate({
      login_id: data.login_id,
      password: data.password,
      // fcm_token: fcmToken,
    });
  };

  const methods = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      login_id: "",
      password: "",
      fcm_token: "",
    },
  });

  const {
    setError,
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  // 엔터 키 눌렸을 때 처리
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const handleClick = () => {
    setDeactivateModal(false);
    const url = "http://pf.kakao.com/_vfRpxj";
    window.open(url, "_blank");
  };

  const handleSignupEmailMutation = () => {
    signupEmailMutation(watch("login_id"));
  };

  const errorMessage = errors.login_id?.message || errors.password?.message;

  return (
    <FormProvider {...methods}>
      <Background>
        <Container onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
          {/* <BackHeader title="" transparent={true} /> */}
          <ContentWrapper>
            <LogoWrapper>
              <Image
                src={theme.image.logo_green_svg.default}
                width="240px"
                height="100%"
              />
            </LogoWrapper>

            <Tooltip
              title={recentLogin === "NORMAL" ? "최근 로그인" : ""}
              open={recentLogin === "NORMAL"}
              color="#FF4808"
              autoAdjustOverflow={false}
              overlayInnerStyle={{
                backgroundColor: "#FF4808",
                borderRadius: "2px ",
                padding: "0.2rem 0.8rem",
                height: "auto",
                minHeight: "auto",
                fontSize: "1.2rem",
                fontFamily: "Pretendard",
              }}
              overlayStyle={{
                left: "2rem", // 툴팁을 오른쪽으로 이동
              }}
              getPopupContainer={(trigger: any) => trigger.parentElement}
            />

            <InputWrapper>
              <CustomTextInput
                name="login_id"
                width="100%"
                mode="email"
                background="transparent"
                isLoginPage={true}
                placeholder="이메일"
                max={100}
              />

              <CustomTextInput
                name="password"
                width="100%"
                type="password"
                background="transparent"
                isLoginPage={true}
                placeholder="비밀번호"
              />
            </InputWrapper>

            {(errors.login_id || errors.password) && (
              <ErrorMessage>
                {(errors.login_id || errors.password) && (
                  <ErrorMessage>{errorMessage as string}</ErrorMessage>
                )}
              </ErrorMessage>
            )}

            <LoginCheckWrapper>
              <Password onClick={() => navigate("/find-password")}>
                {"비밀번호 잊으셨나요?"}
              </Password>
            </LoginCheckWrapper>

            <BtnWrapper>
              <ConfigProvider
                theme={{
                  components: {
                    Tooltip: {
                      zIndexPopup: 0,
                    },
                  },
                }}>
                <BasicButton
                  title="로그인하기"
                  height="5.2rem"
                  width="100%"
                  type="submit"
                />
              </ConfigProvider>
            </BtnWrapper>

            <SignupBtn>
              <BasicButton
                title="가입하기"
                transparent={true}
                fontSize="1.6rem"
                padding="0"
                gray={true}
                onClick={() => navigate("/join")}
              />
            </SignupBtn>

            <DividerContainer>
              <Line />
              <Text>{"SNS 간편로그인"}</Text>
              <Line />
            </DividerContainer>

            <SnsIconWrapper>
              <GoogleLoginButton recentLogin={recentLogin} />
              {/* 안드로이드 기기일 때 Apple 로그인 버튼을 숨김 */}
              {!isAndroid() && <AppleLoginButton recentLogin={recentLogin} />}
            </SnsIconWrapper>
          </ContentWrapper>

          <BasicModal
            open={deactivateModal}
            setOpen={setDeactivateModal}
            activeName="카카오톡 1:1 채팅 문의"
            activeEvent={handleClick}
            notice>
            <Flex vertical gap={"1rem"} justify="column" align="center">
              <span>탈퇴한 계정정보입니다. </span>
              <span>활성화 하시려면</span>
              <span>관리자에게 문의해주시길 바랍니다.</span>
            </Flex>
          </BasicModal>

          <BasicModal
            open={suspendedModalVisible}
            setOpen={setSuspendedModalVisible}
            activeName="카카오톡 1:1 채팅 문의"
            activeEvent={handleClick}
            notice>
            <ModalContent
              mainMessage="관리자에 의해 계정이 정지되었습니다."
              subMessage="관리자에게 문의주시기 바랍니다."
            />
          </BasicModal>

          <BasicModal
            open={modals.activeUser}
            closeModal={() => closeModal("activeUser")}
            activeName="인증 메일 다시 보내기"
            activeEvent={handleSignupEmailMutation}
            notice
            maskClosable={true}>
            <Flex vertical gap={"1rem"} justify="column" align="center">
              <span>회원가입이 완료되지 않았습니다.</span>
              <span>메일함을 확인하여 회원가입을 완료해주세요.</span>
            </Flex>
          </BasicModal>
        </Container>
      </Background>
    </FormProvider>
  );
};

export default LoginPage;

const Background = styled.div(({ theme }) => ({
  width: "100%",
  minHeight: "100dvh",
  backgroundImage: `url(${theme.image.login_background.default})`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  zIndex: 10000,
  overflow: "scroll",

  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const Container = styled.form(({ theme }) => ({
  position: "relative",
  maxWidth: "56rem",
  width: "100dvw",
  height: "100vh",
}));

const ContentWrapper = styled.div(({ theme }) => ({
  padding: "0 2rem",
  position: "absolute",
  width: "100%",
  // minHeight: "100%",
}));

const InputWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
}));

const LoginCheckWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "3rem",
  color: theme.color.light_gray,
  marginTop: "1.4rem",
  gap: "1.6rem",
}));

const LogoWrapper = styled.div(
  () =>
    ({
      // margin: "11.6rem auto 9rem ",
      minHeight: "20rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    }) as const,
);

const Password = styled.div(({ theme }) => ({
  textDecoration: "underline",
}));

const BtnWrapper = styled.div(({ theme }) => ({
  color: theme.color.black,
}));

const SignupBtn = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  marginTop: "2rem",
  color: theme.color.light_gray,
  textDecoration: "underline",
  fontWeight: theme.fontWeight.medium,
  fontSize: "1.6rem",
}));

const DividerContainer = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  minHeight: "10rem",
}));

const Line = styled.div(({ theme }) => ({
  flexGrow: 1,
  height: "1px",
  backgroundColor: theme.color.light_gray,
}));

const Text = styled.span(({ theme }) => ({
  padding: "0 10px",
  color: theme.color.light_gray,
  fontSize: "14px",
  whiteSpace: "nowrap",
}));

const SnsIconWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "2rem",
});

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  marginTop: "0.6rem",
  height: "1.5rem",
}));
